import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { hasMarkdownSyntax, parseMarkdown, type MessageContent, type MessageTextEntity } from "@tg/shared";
import { httpBaseFromWsUrl } from "../../lib/auth-api.js";
import { mediaApi } from "../../lib/media-api.js";
import { ContextMenuPositioner } from "../../components/context-menu.js";
import {
  formatDaySeparator,
  formatFullDateTime,
  formatTime,
  type TimeZonePref
} from "../../lib/datetime.js";
import type { ScheduledMessageViewModel } from "../../lib/store.js";
import {
  resolveChatTranslationLanguage,
  resolveOutgoingLanguage,
  useRealtimeStore
} from "../../lib/store.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";
import { openMediaViewer } from "./media-viewer.js";
import { SchedulePickerModal, type SchedulePickerResult } from "./schedule-picker-modal.js";

// Окно «Отложенные сообщения» — как экран отложенных в оригинальном
// Telegram: отдельная «полка» сообщений чата, сгруппированных по дню
// отправки, с собственным полем ввода внизу (всё, что пишется здесь,
// планируется, а не уходит сразу). ПКМ / кнопка «⋯» на сообщении —
// «Отправить сейчас», «Изменить сообщение», «Изменить время», «Удалить».
// Открывается сразу после «Запланировать» из композера и с кнопки
// «Отложенные» в панели чата.
//
// 2026-10-09 (запрос №3): как в обычном чате — перевод отложенных (кнопка в
// шапке), перевод черновика перед планированием, Ctrl+V картинок с
// предпросмотром и правка уже запланированного (текст/подпись/замена фото,
// время отправки при этом не меняется).

interface ScheduledMessagesModalProps {
  chatId: string;
  chatTitle: string;
  onClose(): void;
}

const EMPTY: ScheduledMessageViewModel[] = [];

const lowerFirst = (value: string): string =>
  value.length > 0 ? value.charAt(0).toLowerCase() + value.slice(1) : value;

const entryMediaUrl = (entry: ScheduledMessageViewModel): string | null => {
  const content = entry.content;
  if (content.kind === "photo" || content.kind === "video") {
    return content.url ?? content.thumbnailUrl ?? null;
  }
  return null;
};

const entryLabel = (entry: ScheduledMessageViewModel): string => {
  const content = entry.content;
  switch (content.kind) {
    case "photo":
      return "Фото";
    case "video":
      return "Видео";
    case "document":
      return content.fileName ? `Файл · ${content.fileName}` : "Файл";
    case "voice":
      return "Голосовое сообщение";
    case "videoNote":
      return "Кружок";
    case "sticker":
      return content.emoji ? `Стикер ${content.emoji}` : "Стикер";
    default:
      return "Сообщение";
  }
};


// --- Картинки из буфера (Ctrl+V) -------------------------------------------
// Окну нужны только изображения, поэтому без общей машинерии вложений чата
// (голосовые/кружки/видео): previewUrl для предпросмотра + размеры для TDLib.
interface PastedImage {
  id: string;
  file: File;
  previewUrl: string;
  width?: number;
  height?: number;
}

const MAX_PASTED_IMAGES = 10;

const readImageSize = (url: string): Promise<{ width?: number; height?: number }> =>
  new Promise((resolve) => {
    const image = new Image();
    const finish = (): void =>
      resolve({
        ...(image.naturalWidth > 0 ? { width: image.naturalWidth } : {}),
        ...(image.naturalHeight > 0 ? { height: image.naturalHeight } : {})
      });
    image.onload = finish;
    image.onerror = finish;
    window.setTimeout(finish, 4_000);
    image.src = url;
  });

const imagesFromClipboard = (event: React.ClipboardEvent): File[] => {
  const items = event.clipboardData?.items;
  const files: File[] = [];
  if (!items) {
    return files;
  }
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item?.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }
  }
  return files;
};

const toPastedImage = async (file: File): Promise<PastedImage> => {
  const previewUrl = URL.createObjectURL(file);
  const size = await readImageSize(previewUrl);
  return { id: Math.random().toString(36).slice(2), file, previewUrl, ...size };
};

const uploadPhotoContent = async (
  wsUrl: string,
  image: PastedImage,
  caption: string
): Promise<MessageContent> => {
  const uploaded = await mediaApi.uploadFile(wsUrl, image.file, "photo");
  return {
    kind: "photo",
    fileId: uploaded.fileId,
    ...(caption ? { caption } : {}),
    ...(image.width ? { width: image.width } : {}),
    ...(image.height ? { height: image.height } : {})
  };
};

// --- Перевод ------------------------------------------------------------------
// Переводы отложенных живут в модульном кэше по (язык, текст): после правки
// текст другой — перевод пересчитается сам, а повторное открытие окна не
// тратит запросы к LLM. Кэш маленький: отложенных у чата единицы.
const scheduledTranslations = new Map<string, string>();
const SCHEDULED_TRANSLATIONS_LIMIT = 500;
const translationCacheKey = (lang: string, text: string): string => `${lang}\u0001${text}`;
const rememberScheduledTranslation = (lang: string, text: string, translated: string): void => {
  if (scheduledTranslations.size >= SCHEDULED_TRANSLATIONS_LIMIT) {
    const oldest = scheduledTranslations.keys().next().value;
    if (oldest !== undefined) {
      scheduledTranslations.delete(oldest);
    }
  }
  scheduledTranslations.set(translationCacheKey(lang, text), translated);
};

const entryText = (entry: ScheduledMessageViewModel): string =>
  entry.content.kind === "text"
    ? entry.text
    : "caption" in entry.content
      ? (entry.content.caption ?? "")
      : "";

// Ссылки [текст](url) обратно в markdown при входе в правку — иначе
// сохранение молча сняло бы их (в поле лежит только видимый текст).
const textWithMarkdownLinks = (text: string, entities: MessageTextEntity[] | undefined): string => {
  const links = (entities ?? [])
    .filter((entity): entity is Extract<MessageTextEntity, { type: "link" }> =>
      entity.type === "link" && entity.url.length > 0
    )
    .sort((left, right) => right.offset - left.offset);
  let result = text;
  for (const link of links) {
    const label = result.slice(link.offset, link.offset + link.length);
    if (label.length === 0 || /[[\]\n]/.test(label)) {
      continue;
    }
    result =
      result.slice(0, link.offset) + `[${label}](${link.url})` + result.slice(link.offset + link.length);
  }
  return result;
};

const isEditableMedia = (content: MessageContent): boolean =>
  content.kind === "photo" || content.kind === "video";

export const ScheduledMessagesModal = ({ chatId, chatTitle, onClose }: ScheduledMessagesModalProps) => {
  const entries = useRealtimeStore((state) => state.scheduledByChat[chatId] ?? EMPTY);
  const isLoading = useRealtimeStore((state) => state.scheduledLoadingByChat[chatId] === true);
  const loadScheduledMessages = useRealtimeStore((state) => state.loadScheduledMessages);
  const sendScheduledNow = useRealtimeStore((state) => state.sendScheduledNow);
  const deleteScheduledMessage = useRealtimeStore((state) => state.deleteScheduledMessage);
  const rescheduleMessage = useRealtimeStore((state) => state.rescheduleMessage);
  const sendMessage = useRealtimeStore((state) => state.sendMessage);
  const isPrivateChat = useRealtimeStore((state) => {
    const chat = state.chats[chatId];
    return Boolean(chat && !chat.isGroupChat && !chat.isSavedMessages);
  });
  const timeZone: TimeZonePref = useUiPrefs((prefs) => prefs.timeZone);
  const editMessage = useRealtimeStore((state) => state.editMessage);
  const wsUrl = useRealtimeStore((state) => state.wsUrl);
  const accountId = useRealtimeStore((state) => state.accountId);
  // Языки — те же, что в шапке чата: «читать» (на него переводим отложенные)
  // и «писать» (на него переводим черновик для клиента).
  const readingLang = useRealtimeStore((state) => resolveChatTranslationLanguage(state, chatId));
  const outgoingLang = useRealtimeStore((state) => resolveOutgoingLanguage(state, chatId));
  const chatTranslated = useRealtimeStore((state) => Boolean(state.translatedChats[chatId]));

  const [draft, setDraft] = useState("");
  const [images, setImages] = useState<PastedImage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Правка уже запланированного: поле ввода занято его текстом/подписью,
  // черновик нового сообщения откладываем и возвращаем после.
  const [editing, setEditing] = useState<{
    entry: ScheduledMessageViewModel;
    draftBefore: string;
  } | null>(null);
  const [replacement, setReplacement] = useState<PastedImage | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [picker, setPicker] = useState<
    | { mode: "compose" }
    | { mode: "reschedule"; messageId: string; initialAt?: string | undefined }
    | null
  >(null);
  const [menu, setMenu] = useState<{ x: number; y: number; messageId: string } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    loadScheduledMessages(chatId);
  }, [chatId, loadScheduledMessages]);

  // objectURL предпросмотров освобождаем при закрытии окна.
  const imagesRef = useRef<PastedImage[]>([]);
  imagesRef.current = replacement ? [...images, replacement] : images;
  useEffect(
    () => () => {
      for (const image of imagesRef.current) {
        URL.revokeObjectURL(image.previewUrl);
      }
    },
    []
  );

  // --- Перевод отложенных (кнопка в шапке) --------------------------------
  // По умолчанию — как в самом чате: включён перевод чата, переводим и здесь.
  const [translateOn, setTranslateOn] = useState(chatTranslated);
  const [translationTick, setTranslationTick] = useState(0);
  const [translating, setTranslating] = useState(false);
  const [hiddenTranslations, setHiddenTranslations] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!translateOn || readingLang.length === 0) {
      return;
    }
    const missing = [
      ...new Set(
        entries
          .filter((entry) => !entry.isPending)
          .map(entryText)
          .map((text) => text.trim())
          .filter(
            (text) => text.length > 0 && !scheduledTranslations.has(translationCacheKey(readingLang, text))
          )
      )
    ];
    if (missing.length === 0) {
      return;
    }
    let cancelled = false;
    setTranslating(true);
    void (async () => {
      try {
        for (let i = 0; i < missing.length; i += 20) {
          const batch = missing.slice(i, i + 20);
          const response = await fetch(`${httpBaseFromWsUrl(wsUrl)}/api/translate`, {
            method: "POST",
            credentials: "same-origin",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ texts: batch, targetLanguage: readingLang, accountId })
          });
          const payload = (await response.json().catch(() => null)) as
            | { ok?: boolean; texts?: unknown; message?: string }
            | null;
          if (
            !response.ok ||
            !payload?.ok ||
            !Array.isArray(payload.texts) ||
            payload.texts.length !== batch.length
          ) {
            throw new Error(
              response.status === 503
                ? "Переводчик не настроен. Включите интеграцию «Переводчик» в Настройках."
                : (payload?.message ?? "Не удалось перевести отложенные сообщения.")
            );
          }
          batch.forEach((text, index) => {
            const translated = (payload.texts as unknown[])[index];
            if (typeof translated === "string") {
              rememberScheduledTranslation(readingLang, text, translated);
            }
          });
          if (!cancelled) {
            setTranslationTick((tick) => tick + 1);
          }
        }
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "Не удалось перевести.");
        }
      } finally {
        if (!cancelled) {
          setTranslating(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, entries, readingLang, translateOn, wsUrl]);
  void translationTick;

  // --- Перевод черновика (как кнопка перевода в композере чата) -----------
  const [outgoingTranslated, setOutgoingTranslated] = useState<{
    original: string;
    translated: string;
    back: string | null;
    corrected: boolean;
  } | null>(null);
  const [isTranslatingDraft, setIsTranslatingDraft] = useState(false);
  const draftIsTranslation = outgoingTranslated !== null && draft === outgoingTranslated.translated;
  const translateDraft = useCallback(async (): Promise<void> => {
    if (draftIsTranslation && outgoingTranslated) {
      // Повторный клик — вернуть оригинал, как в чате.
      setDraft(outgoingTranslated.original);
      setOutgoingTranslated(null);
      return;
    }
    const text = draft.trim();
    if (isTranslatingDraft) return;
    if (text.length === 0) {
      setError("Сначала напишите текст.");
      return;
    }
    if (outgoingLang.length === 0) {
      setError(
        "Не задан язык клиента. Выберите его кнопкой языков в шапке чата или добавьте язык аккаунту в Настройках → Переводчик."
      );
      return;
    }
    setIsTranslatingDraft(true);
    setError(null);
    try {
      const response = await fetch(`${httpBaseFromWsUrl(wsUrl)}/api/translate`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        // Те же параметры, что у композера чата: accountId — для языков
        // гео-админов, verify — сверка обратным переводом (уходит клиенту).
        body: JSON.stringify({
          text,
          targetLanguage: outgoingLang,
          accountId,
          verify: true,
          sourceLanguage: readingLang
        })
      });
      const payload = (await response.json().catch(() => null)) as
        | { ok?: boolean; text?: string; message?: string; back?: string | null; corrected?: boolean }
        | null;
      if (!response.ok || !payload?.ok || typeof payload.text !== "string") {
        setError(
          response.status === 503
            ? "Переводчик не настроен. Включите интеграцию «Переводчик» в Настройках."
            : response.status === 502
              ? `Провайдер перевода вернул ошибку (${payload?.message ?? "нет деталей"}).`
              : (payload?.message ?? "Не удалось перевести текст.")
        );
        return;
      }
      const translated = payload.text.trim();
      if (translated.length === 0) {
        setError("Провайдер вернул пустой перевод — попробуйте ещё раз.");
        return;
      }
      setOutgoingTranslated({
        original: draft,
        translated,
        back: typeof payload.back === "string" && payload.back.trim().length > 0 ? payload.back.trim() : null,
        corrected: payload.corrected === true
      });
      setDraft(translated);
      requestAnimationFrame(() => {
        const target = textareaRef.current;
        target?.focus();
        target?.setSelectionRange(translated.length, translated.length);
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Сетевая ошибка при переводе.");
    } finally {
      setIsTranslatingDraft(false);
    }
  }, [accountId, draft, draftIsTranslation, isTranslatingDraft, outgoingLang, outgoingTranslated, readingLang, wsUrl]);

  // --- Вставка картинок -----------------------------------------------------
  const addImages = async (files: File[]): Promise<void> => {
    if (files.length === 0) return;
    setError(null);
    if (editing) {
      if (!isEditableMedia(editing.entry.content)) {
        setError("К текстовому сообщению фото не добавить — запланируйте новое сообщение с фото.");
        return;
      }
      // Правка: новое фото заменяет текущее (берём последнее из буфера).
      const next = await toPastedImage(files[files.length - 1]!);
      setReplacement((current) => {
        if (current) URL.revokeObjectURL(current.previewUrl);
        return next;
      });
      return;
    }
    const room = MAX_PASTED_IMAGES - images.length;
    if (room <= 0) {
      setError(`Не больше ${MAX_PASTED_IMAGES} картинок за раз.`);
      return;
    }
    const prepared = await Promise.all(files.slice(0, room).map(toPastedImage));
    setImages((current) => [...current, ...prepared]);
  };
  const removeImage = (id: string): void => {
    setImages((current) => {
      const target = current.find((image) => image.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return current.filter((image) => image.id !== id);
    });
  };

  // --- Правка запланированного ---------------------------------------------
  const startEditing = (entry: ScheduledMessageViewModel): void => {
    const text =
      entry.content.kind === "text"
        ? textWithMarkdownLinks(entry.text, entry.content.entities)
        : entryText(entry);
    setEditing({ entry, draftBefore: editing ? editing.draftBefore : draft });
    setReplacement((current) => {
      if (current) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
    setOutgoingTranslated(null);
    setError(null);
    setDraft(text);
    requestAnimationFrame(() => {
      const target = textareaRef.current;
      target?.focus();
      target?.setSelectionRange(text.length, text.length);
    });
  };
  const cancelEditing = (): void => {
    if (!editing) return;
    setDraft(editing.draftBefore);
    setEditing(null);
    setReplacement((current) => {
      if (current) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
    setOutgoingTranslated(null);
  };
  const saveEditing = async (): Promise<void> => {
    if (!editing || busy) return;
    const entry = editing.entry;
    const text = draft.trim();
    if (entry.content.kind === "text" && text.length === 0) {
      setError("Текст сообщения не может быть пустым.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (replacement) {
        const content = await uploadPhotoContent(wsUrl, replacement, "");
        editMessage(chatId, entry.id, text, { content, allowEmpty: true, scheduled: true });
      } else {
        editMessage(chatId, entry.id, text, {
          allowEmpty: entry.content.kind !== "text",
          scheduled: true
        });
      }
      setDraft(editing.draftBefore);
      setEditing(null);
      setReplacement((current) => {
        if (current) URL.revokeObjectURL(current.previewUrl);
        return null;
      });
      setOutgoingTranslated(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось загрузить фото.");
    } finally {
      setBusy(false);
    }
  };

  // Esc: сначала закрываем меню/пикер, потом само окно. Capture — чтобы
  // не закрыть чат под окном.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") {
        return;
      }
      if (picker) {
        return; // пикер обработает сам
      }
      event.stopPropagation();
      event.preventDefault();
      if (menu) {
        setMenu(null);
        return;
      }
      if (editingRef.current) {
        cancelEditingRef.current();
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [menu, onClose, picker]);
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const cancelEditingRef = useRef(cancelEditing);
  cancelEditingRef.current = cancelEditing;

  // Новые записи — прокрутка вниз, как в ленте.
  useEffect(() => {
    const body = bodyRef.current;
    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [entries.length]);

  const groups = useMemo(() => {
    const result: Array<{ key: string; label: string; items: ScheduledMessageViewModel[] }> = [];
    for (const entry of entries) {
      const key = entry.sendWhenOnline ? "online" : entry.sendAt ? entry.sendAt.slice(0, 10) : "unknown";
      const label = entry.sendWhenOnline
        ? "Отправить, когда собеседник будет в сети"
        : entry.sendAt
          ? `Отправить ${lowerFirst(formatDaySeparator(entry.sendAt, timeZone))}`
          : "Время не указано";
      const last = result[result.length - 1];
      if (last && last.key === key) {
        last.items.push(entry);
      } else {
        result.push({ key, label, items: [entry] });
      }
    }
    return result;
  }, [entries, timeZone]);

  const menuEntry = menu ? entries.find((entry) => entry.id === menu.messageId) ?? null : null;

  const scheduleDraft = async (result: SchedulePickerResult): Promise<void> => {
    const text = draft.trim();
    if ((text.length === 0 && images.length === 0) || busy) {
      return;
    }
    const options = result.whenOnline ? { whenOnline: true } : {};
    setPicker(null);
    if (images.length > 0) {
      // Фото уходят отдельными отложенными сообщениями, подпись — у первого
      // (как в чате без «Группировать»). Загрузка — до планирования.
      setBusy(true);
      setError(null);
      const toSend = images;
      try {
        const contents: MessageContent[] = [];
        for (const [index, image] of toSend.entries()) {
          contents.push(await uploadPhotoContent(wsUrl, image, index === 0 ? text : ""));
        }
        for (const content of contents) {
          sendMessage(chatId, content, undefined, result.sendAt, undefined, options);
        }
        for (const image of toSend) {
          URL.revokeObjectURL(image.previewUrl);
        }
        setImages([]);
        setDraft("");
        setOutgoingTranslated(null);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Не удалось загрузить картинку.");
      } finally {
        setBusy(false);
      }
    } else {
      // Разметка и ссылки [текст](url) — как в обычном композере.
      const parsed = hasMarkdownSyntax(text) ? parseMarkdown(text) : { text, entities: [] };
      sendMessage(
        chatId,
        {
          kind: "text",
          text: parsed.text,
          ...(parsed.entities.length > 0 ? { entities: parsed.entities } : {})
        },
        undefined,
        result.sendAt,
        undefined,
        options
      );
      setDraft("");
      setOutgoingTranslated(null);
    }
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  };
  const canSchedule = draft.trim().length > 0 || images.length > 0;
  const submitComposer = (): void => {
    if (editing) {
      void saveEditing();
      return;
    }
    if (canSchedule && !busy) {
      setPicker({ mode: "compose" });
    }
  };

  const openMenuAt = (x: number, y: number, messageId: string): void => {
    setMenu({ x, y, messageId });
  };

  return createPortal(
    <div
      className="forward-modal scheduled-modal-layer"
      role="dialog"
      aria-modal="true"
      aria-label="Отложенные сообщения"
    >
      <button
        type="button"
        className="forward-modal__backdrop"
        onClick={onClose}
        aria-label="Закрыть"
      />
      <div className="forward-modal__panel scheduled-modal">
        <div className="scheduled-modal__header">
          <div>
            <strong>Отложенные сообщения</strong>
            <span>
              {chatTitle}
              {entries.length > 0 ? ` · ${entries.length}` : ""}
            </span>
          </div>
          <div className="scheduled-modal__header-actions">
            <button
              type="button"
              className={
                translateOn
                  ? "button button--ghost scheduled-modal__translate scheduled-modal__translate--on"
                  : "button button--ghost scheduled-modal__translate"
              }
              onClick={() => {
                setError(null);
                setHiddenTranslations(new Set());
                setTranslateOn((value) => !value);
              }}
              disabled={readingLang.length === 0}
              title={
                translateOn
                  ? "Скрыть переводы отложенных сообщений"
                  : `Перевести отложенные сообщения на «${readingLang.toUpperCase()}»`
              }
            >
              <i className="ic ic-translate" aria-hidden="true" />
              {translateOn
                ? translating
                  ? "Переводим…"
                  : "Показать оригинал"
                : `Перевести (${readingLang.toUpperCase()})`}
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => loadScheduledMessages(chatId)}
              disabled={isLoading}
              aria-label="Обновить"
              title="Обновить список"
            >
              <i className="ic ic-refresh" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Закрыть"
              title="Закрыть (Esc)"
            >
              <i className="ic ic-close" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="scheduled-modal__body" ref={bodyRef}>
          {isLoading && entries.length === 0 ? (
            <div className="scheduled-modal__empty">Загружаем…</div>
          ) : entries.length === 0 ? (
            <div className="scheduled-modal__empty">
              <i className="ic ic-schedule" aria-hidden="true" />
              <strong>Отложенных сообщений нет</strong>
              <span>
                Напишите сообщение ниже и выберите время — оно уйдёт само, даже если
                вкладка будет закрыта.
              </span>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="scheduled-modal__group">
                <div className="scheduled-modal__day">{group.label}</div>
                {group.items.map((entry) => {
                  const mediaUrl = entryMediaUrl(entry);
                  const caption =
                    "caption" in entry.content ? entry.content.caption ?? "" : "";
                  const text = entry.content.kind === "text" ? entry.text : caption;
                  return (
                    <div
                      key={entry.id}
                      className={[
                        "scheduled-bubble",
                        entry.isPending ? "scheduled-bubble--pending" : "",
                        editing?.entry.id === entry.id ? "scheduled-bubble--editing" : ""
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        if (entry.isPending) return;
                        openMenuAt(event.clientX, event.clientY, entry.id);
                      }}
                    >
                      {mediaUrl ? (
                        <button
                          type="button"
                          className="scheduled-bubble__media"
                          onClick={() =>
                            openMediaViewer(
                              [
                                {
                                  kind: entry.content.kind === "video" ? "video" : "photo",
                                  url:
                                    (entry.content.kind === "video" || entry.content.kind === "photo") &&
                                    entry.content.url
                                      ? entry.content.url
                                      : mediaUrl,
                                  ...(entry.content.kind === "video" && entry.content.mimeType
                                    ? { mimeType: entry.content.mimeType }
                                    : {}),
                                  ...(caption ? { caption } : {}),
                                  senderLabel: "Отложенное"
                                }
                              ],
                              0
                            )
                          }
                          title="Открыть"
                        >
                          <img src={mediaUrl} alt={caption || entryLabel(entry)} loading="lazy" />
                        </button>
                      ) : entry.content.kind !== "text" ? (
                        <div className="scheduled-bubble__label">{entryLabel(entry)}</div>
                      ) : null}
                      {text ? (
                        <div className="scheduled-bubble__text" dir="auto">
                          {text}
                        </div>
                      ) : null}
                      {(() => {
                        // Перевод — как под сообщением в чате: блок под
                        // оригиналом с подписью языка и «скрыть».
                        const translated =
                          translateOn && text.trim().length > 0 && !hiddenTranslations.has(entry.id)
                            ? scheduledTranslations.get(translationCacheKey(readingLang, text.trim()))
                            : undefined;
                        if (!translated) return null;
                        return (
                          <div className="message-bubble__translation scheduled-bubble__translation">
                            <div className="message-bubble__translation-label">
                              Перевод ({readingLang}) ·{" "}
                              <button
                                type="button"
                                className="message-bubble__translation-clear"
                                onClick={() =>
                                  setHiddenTranslations((current) => new Set(current).add(entry.id))
                                }
                              >
                                скрыть
                              </button>
                            </div>
                            <div dir="auto">{translated}</div>
                          </div>
                        );
                      })()}
                      <div className="scheduled-bubble__meta">
                        <span
                          title={
                            entry.sendAt ? formatFullDateTime(entry.sendAt, timeZone) : undefined
                          }
                        >
                          {entry.isPending
                            ? entry.id.startsWith("optimistic:")
                              ? "планируем…"
                              : "сохраняем…"
                            : entry.sendWhenOnline
                              ? "когда в сети"
                              : entry.sendAt
                                ? formatTime(entry.sendAt, timeZone)
                                : ""}
                        </span>
                        <i className="ic ic-schedule" aria-hidden="true" />
                        {!entry.isPending ? (
                          <button
                            type="button"
                            className="scheduled-bubble__more"
                            onClick={(event) => {
                              const rect = event.currentTarget.getBoundingClientRect();
                              openMenuAt(rect.left, rect.bottom + 4, entry.id);
                            }}
                            aria-label="Действия"
                            title="Отправить сейчас · Изменить сообщение · Изменить время · Удалить"
                          >
                            <i className="ic ic-more" aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <form
          className="scheduled-modal__composer"
          onSubmit={(event) => {
            event.preventDefault();
            submitComposer();
          }}
        >
          {error ? <div className="side-card__error scheduled-modal__error">{error}</div> : null}
          {editing ? (
            <div className="scheduled-modal__editbar">
              <i className="ic ic-edit" aria-hidden="true" />
              <div className="scheduled-modal__editbar-text">
                <strong>Изменение сообщения</strong>
                <span>
                  {isEditableMedia(editing.entry.content)
                    ? "Текст подписи — в поле ниже. Новое фото — Ctrl+V или «Заменить фото»."
                    : "Время отправки останется прежним."}
                </span>
              </div>
              {isEditableMedia(editing.entry.content) ? (
                <>
                  {replacement || entryMediaUrl(editing.entry) ? (
                    <img
                      className="scheduled-modal__editbar-thumb"
                      src={replacement?.previewUrl ?? entryMediaUrl(editing.entry) ?? ""}
                      alt={replacement ? "Новое фото" : "Текущее фото"}
                      title={replacement ? "Новое фото — заменит текущее при сохранении" : "Текущее фото"}
                    />
                  ) : null}
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Заменить фото
                  </button>
                  {replacement ? (
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() =>
                        setReplacement((current) => {
                          if (current) URL.revokeObjectURL(current.previewUrl);
                          return null;
                        })
                      }
                    >
                      Оставить прежнее
                    </button>
                  ) : null}
                </>
              ) : null}
              <button
                type="button"
                className="icon-button"
                onClick={cancelEditing}
                aria-label="Отменить изменение"
                title="Отменить изменение (Esc)"
              >
                <i className="ic ic-close" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          {!editing && images.length > 0 ? (
            <div className="scheduled-modal__attachments">
              {images.map((image) => (
                <div key={image.id} className="scheduled-modal__attachment">
                  <img src={image.previewUrl} alt="Картинка из буфера" />
                  <button
                    type="button"
                    className="scheduled-modal__attachment-remove"
                    onClick={() => removeImage(image.id)}
                    aria-label="Убрать картинку"
                    title="Убрать"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {outgoingTranslated && draftIsTranslation && outgoingTranslated.back ? (
            <div className="composer__translate-hint">
              {outgoingTranslated.corrected ? (
                <span className="composer__translate-hint-mark">✓ проверено и исправлено · </span>
              ) : (
                <span className="composer__translate-hint-mark">✓ проверено · </span>
              )}
              клиент прочтёт: «{outgoingTranslated.back}»
            </div>
          ) : null}
          <div className="scheduled-modal__composer-row">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = "";
                void addImages(files);
              }}
            />
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onPaste={(event) => {
                const files = imagesFromClipboard(event);
                if (files.length > 0) {
                  event.preventDefault();
                  void addImages(files);
                }
              }}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  event.shiftKey &&
                  (event.ctrlKey || event.metaKey) &&
                  !event.nativeEvent.isComposing
                ) {
                  // Как в композере чата: Ctrl/Cmd+Shift+Enter — перевести.
                  event.preventDefault();
                  void translateDraft();
                  return;
                }
                if (event.key === "Enter" && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
                  event.preventDefault();
                  submitComposer();
                }
              }}
              placeholder={
                editing
                  ? isEditableMedia(editing.entry.content)
                    ? "Подпись к фото…"
                    : "Текст сообщения…"
                  : images.length > 0
                    ? "Подпись к картинке…"
                    : "Отложенное сообщение… (Ctrl+V — вставить картинку)"
              }
              rows={1}
              className="scheduled-modal__textarea"
              autoFocus
            />
            <button
              type="button"
              className={draftIsTranslation ? "cbtn composer__translate-draft composer__translate-draft--on" : "cbtn composer__translate-draft"}
              onClick={() => void translateDraft()}
              disabled={isTranslatingDraft || draft.trim().length === 0}
              aria-label="Перевести текст перед планированием"
              title={
                draftIsTranslation
                  ? "Вернуть в поле оригинал"
                  : outgoingLang.length === 0
                    ? "Выберите язык клиента: кнопка языков в шапке чата"
                    : `Перевести написанное на «${outgoingLang.toUpperCase()}» (Ctrl+Shift+Enter). Повторный клик вернёт оригинал.`
              }
            >
              <i className="ic ic-translate" aria-hidden="true" />
            </button>
            {editing ? (
              <button type="submit" className="button" disabled={busy}>
                {busy ? "Сохраняем…" : "Сохранить"}
              </button>
            ) : (
              <button
                type="submit"
                className="button"
                disabled={!canSchedule || busy}
                title="Выбрать время отправки"
              >
                <i className="ic ic-schedule" aria-hidden="true" /> {busy ? "Загружаем…" : "Запланировать"}
              </button>
            )}
          </div>
        </form>
      </div>

      {menu && menuEntry
        ? createPortal(
            <div
              className="dialog-context-menu__layer scheduled-modal__menu-layer"
              onClick={() => setMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setMenu(null);
              }}
              role="presentation"
            >
              <ContextMenuPositioner anchorX={menu.x} anchorY={menu.y}>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    sendScheduledNow(chatId, menuEntry.id);
                    setMenu(null);
                  }}
                >
                  <i className="ic ic-send" aria-hidden="true" />
                  Отправить сейчас
                </button>
                {menuEntry.content.kind === "text" || isEditableMedia(menuEntry.content) ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="dialog-context-menu__item"
                    onClick={() => {
                      startEditing(menuEntry);
                      setMenu(null);
                    }}
                  >
                    <i className="ic ic-edit" aria-hidden="true" />
                    Изменить сообщение
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    setPicker({
                      mode: "reschedule",
                      messageId: menuEntry.id,
                      initialAt: menuEntry.sendAt
                    });
                    setMenu(null);
                  }}
                >
                  <i className="ic ic-schedule" aria-hidden="true" />
                  Изменить время
                </button>
                <div className="dialog-context-menu__divider" />
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item dialog-context-menu__item--danger"
                  onClick={() => {
                    if (window.confirm("Удалить отложенное сообщение?")) {
                      deleteScheduledMessage(chatId, menuEntry.id);
                    }
                    setMenu(null);
                  }}
                >
                  <i className="ic ic-delete" aria-hidden="true" />
                  Удалить
                </button>
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}

      {picker ? (
        <SchedulePickerModal
          title={picker.mode === "reschedule" ? "Изменить время" : "Отправить позже"}
          submitLabel={picker.mode === "reschedule" ? "Перенести" : "Запланировать"}
          initialAt={picker.mode === "reschedule" ? picker.initialAt : undefined}
          timeZone={timeZone}
          allowWhenOnline={isPrivateChat}
          onClose={() => setPicker(null)}
          onSubmit={(result) => {
            if (picker.mode === "reschedule") {
              rescheduleMessage(chatId, picker.messageId, result);
              setPicker(null);
              return;
            }
            void scheduleDraft(result);
          }}
        />
      ) : null}
    </div>,
    document.body
  );
};
